import { useMemo } from 'react';

import getEnabledItems from '@lyearn/core/utils/array/getEnabledItems';
import { useTranslation } from '@lyearn/i18n';
import { EditOutline, EyeClose, EyeOutline, Pin2, Trash2Outline } from '@lyearn/icons';
import { ActionMenuItemProps } from '@lyearn/molecules';
import useWorkspaceAccessControl from '@/components/WorkspaceAccessControl/hooks/useWorkspaceAccessControl';
import { ChannelSubjectsEnum, WorkspaceActions, WorkspaceSubjects } from '@/types/schema';

export enum MessageActions {
  Edit = 'EDIT',
  Delete = 'DELETE',
  Pin = 'PIN',
  Unpin = 'UNPIN',
  Hide = 'HIDE',
  Unhide = 'UNHIDE',
  ToggleDelete = 'TOGGLE_DELETE',
}

function useMessageAction(
  userId: string,
  postType: ChannelSubjectsEnum,
  {
    createdById,
    pinnedById,
    hiddenById,
  }: {
    createdById: string;
    pinnedById?: string | null;
    hiddenById?: string | null;
  },
  enabledActions?: MessageActions[],
) {
  const subject = postType as unknown as WorkspaceSubjects;
  const { canAccess: canDelete } = useWorkspaceAccessControl(WorkspaceActions.Remove, subject);
  const { canAccess: canPin } = useWorkspaceAccessControl(WorkspaceActions.Pin, subject);
  const { canAccess: canHide } = useWorkspaceAccessControl(WorkspaceActions.Hide, subject);
  const { t } = useTranslation();

  const actions = useMemo(() => {
    const isAuthor = userId === createdById;

    return getEnabledItems<ActionMenuItemProps>([
      {
        label: t('Common:Actions.Edit'),
        id: MessageActions.Edit,
        EndIcon: EditOutline,
        enabled: isAuthor,
      },
      {
        label: t('Common:Message.Pin'),
        id: MessageActions.Pin,
        EndIcon: Pin2,
        enabled: canPin && !pinnedById && !hiddenById,
      },
      {
        label: t('Common:Message.Unpin'),
        id: MessageActions.Unpin,
        EndIcon: Pin2,
        enabled: canPin && !!pinnedById,
      },
      {
        label: t('Common:Message.Hide'),
        id: MessageActions.Hide,
        EndIcon: EyeClose,
        enabled: canHide && !hiddenById,
      },
      {
        label: t('Common:Message.Unhide'),
        id: MessageActions.Unhide,
        EndIcon: EyeOutline,
        enabled: canHide && !!hiddenById,
      },
      {
        label: t('Common:Actions.Delete'),
        id: MessageActions.ToggleDelete,
        EndIcon: Trash2Outline,
        appearance: 'danger',
        enabled: canDelete || isAuthor,
      },
    ]).filter((action) =>
      enabledActions ? enabledActions.includes(action.id as MessageActions) : true,
    );
  }, [canDelete, canHide, canPin, createdById, enabledActions, hiddenById, pinnedById, t, userId]);

  return actions;
}

export default useMessageAction;
